import { NavLink } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { longDate, zonedAbbreviation, zonedTime } from '../lib/dates';
import { useMinuteTick } from '../hooks/useClock';
import { TeacherMark } from './TeacherMark';
import { sectionColors, type SectionColor } from '../theme/palette';
import { orderNav, visibleNav } from '@td/shared';

export interface NavItem {
  to: string;
  label: string;
  icon: string;
}

/** Inline SVG paths keep the shell dependency-free; swap for an icon set later. */
const ICONS: Record<string, string> = {
  home: 'M3 10.5 12 3l9 7.5V21a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1z',
  calendar:
    'M7 3v3M17 3v3M3 9h18M5 5h14a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z',
  book: 'M4 5a2 2 0 0 1 2-2h13v18H6a2 2 0 0 1-2-2zM9 3v18',
  check: 'M9 11l3 3 6-6M4 5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z',
  users:
    'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM22 21v-2a4 4 0 0 0-3-3.87',
  seat: 'M4 4h16v6H4zM4 14h16v6H4zM8 10v4M16 10v4',
  chart: 'M4 20V10M10 20V4M16 20v-7M22 20H2',
  bookmark: 'M6 3h12a1 1 0 0 1 1 1v17l-7-4-7 4V4a1 1 0 0 1 1-1z',
  note: 'M6 3h9l5 5v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zM15 3v5h5M8 12h8M8 16h5',
  clipboard:
    'M9 3h6a1 1 0 0 1 1 1v1H8V4a1 1 0 0 1 1-1zM8 5H6a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1h-2M9 11h6M9 15h4',
  folder: 'M3 7a1 1 0 0 1 1-1h5l2 2h9a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z',
  camera:
    'M4 7h3l1.5-2h7L17 7h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1zM12 16a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z',
  // A star on a badge: the jobs board is the one place in the room where being
  // picked is the point.
  badge:
    'M12 3l2.1 4.3 4.7.7-3.4 3.3.8 4.7-4.2-2.2-4.2 2.2.8-4.7L5.2 8l4.7-.7zM7 17v4l5-2 5 2v-4',
  settings:
    'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68 1.65 1.65 0 0 0 10 3.17V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z',
};

/**
 * One hue per section, taken from whichever theme is in force.
 *
 * Derived rather than listed: ten themes times nine sections would be three
 * hundred and sixty hand-picked colours, and every new theme would mean
 * picking thirty-six more.
 */
export type NavColor = SectionColor;

export function navColors(): Record<string, NavColor> {
  return sectionColors();
}

export const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: 'home' },
  { to: '/calendar', label: 'Calendar', icon: 'calendar' },
  { to: '/lesson-plans', label: 'Lesson plans', icon: 'book' },
  { to: '/sub-plans', label: 'Sub plans', icon: 'clipboard' },
  { to: '/todos', label: 'To-do list', icon: 'check' },
  { to: '/roster', label: 'Student roster', icon: 'users' },
  { to: '/seating', label: 'Seating chart', icon: 'seat' },
  { to: '/jobs', label: 'Student jobs', icon: 'badge' },
  { to: '/photos', label: 'Photo book', icon: 'camera' },
  { to: '/documents', label: 'Documents', icon: 'folder' },
  { to: '/data', label: 'Data', icon: 'chart' },
  { to: '/notes', label: 'Notes', icon: 'note' },
  { to: '/bookmarks', label: 'Bookmarks', icon: 'bookmark' },
  { to: '/settings', label: 'Settings', icon: 'settings' },
];

/**
 * The date and the time, in whatever zone Settings is set to.
 *
 * Split out so the minute tick repaints eight words rather than the whole
 * navigation rail.
 */
function Clock() {
  useMinuteTick();
  const zone = zonedAbbreviation();

  return (
    <p className="mt-0.5 text-xs text-stone">
      {longDate()}
      <span className="mt-0.5 block text-espresso">
        {zonedTime()}
        {zone && <span className="ml-1 text-stone">{zone}</span>}
      </span>
    </p>
  );
}

export function Icon({ name, size = 18 }: { name: string; size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      width={size}
      height={size}
      className="shrink-0"
      aria-hidden="true"
    >
      <path d={ICONS[name] ?? ICONS.home!} />
    </svg>
  );
}

export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { user, signOut } = useAuth();

  // Recomputed on render, which is exactly when the theme can have changed.
  const colors = navColors();

  const hasPhoto = user?.settings.avatarMark === 'photo' && user.hasPhoto;

  /*
   * Her order, if she has one.
   *
   * The shipped list stays the shipped list — `orderNav` returns a rearranged
   * copy — so a page added in a later update still knows where it belongs and
   * still turns up, even for a teacher who arranged this a year ago.
   */
  const items = visibleNav(orderNav(NAV_ITEMS, user?.settings.navOrder), user?.settings.navHidden);

  return (
    <nav
      aria-label="Main"
      className="app-sidebar flex h-full w-60 shrink-0 flex-col overflow-y-auto border-r border-sand px-3 py-5"
    >
      {/*
        A photograph gets a photograph's worth of room.
        
        A drawn mark is punctuation beside a name and belongs on the same
        line as it. A photo of a person is not — at the size of a full stop
        it is an unrecognisable smudge, which is exactly what shipping both
        at 20px produced. So the header changes shape: a real avatar above
        the name when there is a photo, an inline mark when there is not.
      */}
      {hasPhoto ? (
        <div className="flex flex-col items-center px-3 pb-5 text-center">
          <TeacherMark user={user} size={72} className="mb-2 shadow-soft" />
          <p className="font-display text-base leading-tight text-espresso">
            {user?.displayName ?? 'Teacher'}
          </p>
          <Clock />
        </div>
      ) : (
        <div className="px-3 pb-5">
          <p className="flex items-center gap-2 font-display text-base leading-tight text-espresso">
            <TeacherMark user={user} size={20} />
            {user?.displayName ?? 'Teacher'}
          </p>
          <Clock />
        </div>
      )}

      <ul className="flex-1 space-y-0.5">
        {items.map((item) => {
          const color = colors[item.to] ?? colors['/']!;
          return (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.to === '/'}
                onClick={onNavigate}
                /*
                 * Three steps, always in the same direction: the section's
                 * colour is faintly there at rest, a shade stronger under the
                 * cursor, and filled when you are on that page. A row that is
                 * blank until you touch it gives no clue what colour it is.
                 */
                style={({ isActive }) =>
                  isActive
                    ? { backgroundColor: color.deep, color: '#F7F3ED' }
                    : { backgroundColor: color.wash, color: color.label }
                }
                onMouseEnter={(e) => {
                  if (!e.currentTarget.classList.contains('is-active'))
                    e.currentTarget.style.backgroundColor = color.tint;
                }}
                onMouseLeave={(e) => {
                  if (!e.currentTarget.classList.contains('is-active'))
                    e.currentTarget.style.backgroundColor = color.wash;
                }}
                className={({ isActive }) =>
                  [
                    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                    isActive ? 'is-active' : '',
                  ].join(' ')
                }
              >
                <Icon name={item.icon} />
                {item.label}
              </NavLink>
            </li>
          );
        })}
      </ul>

      <button
        onClick={() => void signOut()}
        className="mt-4 rounded-lg px-3 py-2 text-left text-sm text-stone transition-colors hover:bg-sand/60 hover:text-espresso"
      >
        Sign out
      </button>
    </nav>
  );
}
